import React, {FC, useEffect, useState} from 'react'
import {shallowEqual, useSelector} from 'react-redux'
import {postRequest} from '../../../../setup/redux/common/cruds'
import {RootState} from '../../../../setup'
import {handleNotificationWithToastify} from '../../../services/Functional'
import {ONTOUR_STATUS_OPTIONS, ontourStatusTakesCharge, ontourStatusTone} from './ontourStatus'

export type OnTourService =
  | 'hotel'
  | 'custom_hotel'
  | 'transfer'
  | 'sight-seeing'
  | 'custom'
  | 'package_itinerary'

interface Props {
  service: OnTourService
  id: number
  status?: string | null
  chargeAmount?: number | null
  chargeCurrency?: string | null
  // The supplier's currency for this service, used as the default when a charge is entered.
  defaultCurrency?: string
  onSaved?: () => void
}

// Records what happened to a service once the tour ran. The partial-charge codes need an
// amount, so those save on an explicit click; the rest save as soon as they are picked.
export const OnTourStatusSelect: FC<Props> = ({
  service,
  id,
  status,
  chargeAmount,
  chargeCurrency,
  defaultCurrency,
  onSaved,
}) => {
  // Same source every other currency picker uses. The parent accordion dispatches
  // getCurrencyDropdown once, rather than each of these rows firing the same request.
  const {currenciesDropdown}: any = useSelector<RootState>(
    (state) => ({currenciesDropdown: state.suppliers.currenciesDropdown}),
    shallowEqual
  )

  const [value, setValue] = useState(status ?? '')
  // The last status the server accepted. A failed save falls back to it, so the dropdown
  // never shows a status that was not stored.
  const [savedValue, setSavedValue] = useState(status ?? '')
  const [amount, setAmount] = useState(chargeAmount != null ? String(chargeAmount) : '')
  const [currency, setCurrency] = useState(chargeCurrency ?? defaultCurrency ?? 'USD')
  const [saving, setSaving] = useState(false)

  // The dashboard lists reuse row components when the date or page changes, so the state above
  // must follow the row it now renders. Otherwise the dropdown shows the previous row's status,
  // and picking that same status fires no change and never saves.
  useEffect(() => {
    setValue(status ?? '')
    setSavedValue(status ?? '')
    setAmount(chargeAmount != null ? String(chargeAmount) : '')
    setCurrency(chargeCurrency ?? defaultCurrency ?? 'USD')
  }, [id, status, chargeAmount, chargeCurrency, defaultCurrency])

  // Keep the selected currency visible while the list is still loading, and cover a service
  // whose stored currency is no longer in the active list.
  const currencyOptions: string[] = Array.from(
    new Set<string>([
      ...(currenciesDropdown ?? []).map((c: any) => c.name).filter(Boolean),
      ...(currency ? [currency] : []),
    ])
  )

  const needsCharge = ontourStatusTakesCharge(value)

  const save = async (nextStatus: string, nextAmount?: string) => {
    setSaving(true)
    try {
      // The blank option clears the status.
      const payload: any = {service, id, ontour_status: nextStatus || null}
      if (ontourStatusTakesCharge(nextStatus)) {
        payload.ontour_charge_amount = Number(nextAmount)
        payload.ontour_charge_currency = currency
      }
      const response = await postRequest(
        `${process.env.REACT_APP_API_BASE_URL}bookings/set-ontour-status`,
        payload
      )
      handleNotificationWithToastify(response.data)
      if (response.data?.success) {
        setSavedValue(nextStatus)
        onSaved?.()
      } else {
        setValue(savedValue)
      }
    } catch (e) {
      setValue(savedValue)
      handleNotificationWithToastify({message: 'Could not update OnTour status', success: false})
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = (next: string) => {
    setValue(next)
    // A partial charge is incomplete until an amount is entered, so wait for the Save click.
    if (ontourStatusTakesCharge(next)) return
    save(next)
  }

  return (
    <div className='d-flex flex-wrap align-items-center gap-1'>
      <span className={`badge badge-light-${ontourStatusTone(status)} me-1`}>OnTour</span>
      <select
        className='form-select form-select-sm w-auto'
        value={value}
        disabled={saving}
        onChange={(e) => handleStatusChange(e.target.value)}
      >
        {ONTOUR_STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {needsCharge && (
        <>
          <input
            className='form-control form-control-sm w-auto'
            type='number'
            min='0'
            step='0.01'
            placeholder='Charge'
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{maxWidth: 110}}
          />
          <select
            className='form-select form-select-sm w-auto'
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            style={{maxWidth: 90}}
          >
            {currencyOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </>
      )}
      {needsCharge && (
        <button
          className='btn btn-sm btn-primary'
          disabled={saving || !(Number(amount) > 0)}
          onClick={() => save(value, amount)}
        >
          Save
        </button>
      )}
    </div>
  )
}
